import React, { useMemo } from 'react';

import Conditional from '../../../common/conditional';
import CustomSection from '../../custom-general/custom-section';

import { concatClassNames, formatNumberRange, getAnomalyClassStyle, getAnomalyFieldColor, getAnomalyTypeClassFromAnomaly, getAnomalyTypeStyle, getChunkedAnomalySummaryItems, getIsAnomalyType, getIsAnomalyWithCMD, getMaxAnomalyType, getMaxClassFromInspectedItem, renderCell, renderStyle } from '../../../../utils/utilities';
import { Column, FVIPipingItem } from '../../../../utils/models';

import classNames from './style.module.scss';
import useProjectDetails from '../../../../hooks/useProjectDetails';

interface PipingAnomalySummaryItemProps {
    pipingItem: FVIPipingItem;
    includeSeparator: boolean;
	sectionTitle: string;
	sectionSubtitle?: string;
}

const PipingAnomalySummaryItem: React.FC<PipingAnomalySummaryItemProps> = ({ pipingItem, includeSeparator, sectionTitle, sectionSubtitle }) => {
	const { projectDetails } = useProjectDetails();

	const isAnomalyType = useMemo(() => {
		return projectDetails && getIsAnomalyType(projectDetails);
	}, [projectDetails]);
	
	const isAnomalyWithCMD = useMemo(() => {
		return projectDetails && getIsAnomalyWithCMD(projectDetails);
	}, [projectDetails]);


	const anomalySummaryColumnsTop: Column[] = [
		(isAnomalyType || isAnomalyWithCMD)
		? {
			path: 'class',
			label: `${isAnomalyWithCMD ? 'Condition' : 'Anomaly'} Class`,
			content: (item: FVIPipingItem) => <span>{getAnomalyTypeClassFromAnomaly(getMaxAnomalyType(item))}</span>,
			style: (item: FVIPipingItem) => getAnomalyTypeStyle({ class: getMaxAnomalyType(item) })
			} 
		: {
			path: 'class',
			label: 'Damage Class',
			content: (item: FVIPipingItem) => <span>{getMaxClassFromInspectedItem(item)}</span>,
			style: (item: FVIPipingItem) => getAnomalyClassStyle({ class: getMaxClassFromInspectedItem(item)})
		},
		{
			path: 'anomalySummary.pipeBody',
			label: 'Pipe Body Condition',
			style: (item) => getAnomalyFieldColor(item.anomalySummary.pipeBody)
		},
		{
			path: 'anomalySummary.noOfPoints',
			label: 'No. of Anomaly In Body',
		},
		{
			path: 'anomalySummary.pitting',
			label: 'Pitting Condition',
		},
		{
			path: 'anomalySummary.noOfPittingLocations',
			label: 'No. of Pitting Locations',
		},
		{
			path: 'anomalySummary.flanges',
			label: 'Flange & Fasteners Condition',
			style: (item) => getAnomalyFieldColor(item.anomalySummary.flanges)
		},
		{
			path: 'anomalySummary.noOfFlanges',
			label: 'No. of Flanges (Have Anomalies)',
		},
		{
			path: 'anomalySummary.valves',
			label: 'Valves Condition',
			style: (item) => getAnomalyFieldColor(item.anomalySummary.valves)
		},
		{
			path: 'anomalySummary.noOfValves',
			label: 'No. of Valves (Have Anomalies)',
		},
	];

	const anomalySummaryColumnsBottom: Column[] = [
		{
			path: 'anomalySummary.corrosionUnderPipeSupport',
			label: 'Corrosion Under Pipe Support (CUPS)',
			style: (item) => getAnomalyFieldColor(item.anomalySummary.corrosionUnderPipeSupport)
		},
		{
			path: 'anomalySummary.noOfCups',
			label: 'No. of CUPS (Have Anomalies)',
		},
		{
			path: 'anomalySummary.fasteners',
			label: 'Pipe Support Condition',
			style: (item) => getAnomalyFieldColor(item.anomalySummary.fasteners)
		},
		{
			path: 'anomalySummary.compositeWrapOnLine',
			label: 'Composite Wrap on Line',
		},
		{
			path: 'anomalySummary.noOfWrapPoints',
			label: 'No. of Wrap Points',
		},
		{
			path: 'anomalySummary.conditionOfSmallBorePipings',
			label: 'Small Bore Pipings Condition',
			style: (item) => getAnomalyFieldColor(item.anomalySummary.conditionOfSmallBorePipings)
		},
		{
			path: 'anomalySummary.noOfSmallBorePipings',
			label: 'No. of Small Bore Pipings (Have Anomalies)',
		},
		{
			path: 'anomalySummary.others',
			label: 'Other (If any)',
		},
		{
			path: 'anomalySummary.description',
			label: 'Description',
		},
		{
			path: 'anomalySummary.extentOfCoatingBreakdown',
			label: 'Extent of Coating Breakdown',
		},
	];

	const paginatedAnomalyItems = useMemo(() => {
		return getChunkedAnomalySummaryItems(pipingItem);
	}, [pipingItem]);

    const classStyle = getAnomalyClassStyle({ class: getMaxClassFromInspectedItem(pipingItem)});

    return (
		<React.Fragment>
			<Conditional isVisible={paginatedAnomalyItems.length > 0}>
				<CustomSection  sectionTitle={sectionTitle} sectionSubtitle={sectionSubtitle} identifier={pipingItem.lineNumber}>
					<div className={concatClassNames([classNames.anomalySummaryItem, "p-1 my-1"])}>
							<Conditional isVisible={includeSeparator}>
								<div className={classNames.anomalySummaryItemDivider} style={classStyle}></div>
							</Conditional>

							<div className={classNames.anomalySummaryItemHeader}>
								<div className={classNames.anomalySummaryItemHeaderSection}><span className="me-2">Sr. No.</span> {pipingItem.id}</div>
								<div 
									className={classNames.anomalySummaryItemHeaderSectionLine} 
									style={{ ...classStyle, textAlign: 'left' }}>
									<span className="me-2">Line Number: </span>{pipingItem.lineNumber}
								</div>
							</div>

							<div className="my-1 table-responsive">
								<table>
									<thead>
										<tr>
											{anomalySummaryColumnsTop.map((column) => (
												<th key={column.path}>{column.label}</th>
											))}
										</tr>
									</thead>
									<tbody>
										<tr>
											{anomalySummaryColumnsTop.map((column: Column) => (
												<td key={column.path} style={renderStyle(pipingItem, column)}>
													{renderCell(pipingItem, column)}
												</td>
											))}
										</tr>
									</tbody>
								</table>
							</div>
							
							<div className="my-1 table-responsive">
								<table>
									<thead>
										<tr>
											{anomalySummaryColumnsBottom.map((column) => (
												<th key={column.path}>{column.label}</th>
											))}
										</tr>
									</thead>
									<tbody>
										<tr>
											{anomalySummaryColumnsBottom.map((column: Column) => (
												<td key={column.path} style={renderStyle(pipingItem, column)}>
													{renderCell(pipingItem, column)}
												</td>
											))}
										</tr>
									</tbody>
								</table>
							</div>
					</div>
				</CustomSection>
			</Conditional>

			<Conditional isVisible={paginatedAnomalyItems.length === 0}>
				<CustomSection sectionTitle={sectionTitle} sectionSubtitle={sectionSubtitle} identifier={pipingItem.lineNumber}>
					<div className={concatClassNames([classNames.anomalySummaryItem, "p-1 my-1"])}>
						<Conditional isVisible={includeSeparator}>
							<div className={classNames.anomalySummaryItemDivider} style={classStyle}></div>
						</Conditional>

						<div className={classNames.anomalySummaryItemHeader}>
							<div className={classNames.anomalySummaryItemHeaderSection}><span className="me-2">Sr. No.</span> {pipingItem.id}</div>
							<div 
								className={classNames.anomalySummaryItemHeaderSectionLine} 
								style={{ ...classStyle, textAlign: 'left' }}>
								<span className="me-2">Line Number: </span>{pipingItem.lineNumber}
							</div>
						</div>
						<div className="my-1 table-responsive">
							<table>
								<thead>
									<tr>
										{anomalySummaryColumnsTop.map((column) => (
											<th key={column.path}>{column.label}</th>
										))}
									</tr>
								</thead>
								<tbody>
									<tr>
										{anomalySummaryColumnsTop.map((column: Column) => (
											<td key={column.path} style={renderStyle(pipingItem, column)}>
												{renderCell(pipingItem, column)}
											</td>
										))}
									</tr>
								</tbody>
							</table>
						</div>
						<div className="my-1 table-responsive">
							<table>
								<thead>
									<tr>
										{anomalySummaryColumnsBottom.map((column) => (
											<th key={column.path}>{column.label}</th>
										))}
									</tr>
								</thead>
								<tbody>
									<tr>
										{anomalySummaryColumnsBottom.map((column: Column) => (
											<td key={column.path} style={renderStyle(pipingItem, column)}>
												{renderCell(pipingItem, column)}
											</td>
										))}
									</tr>
								</tbody>
							</table>
						</div>

						<Conditional isVisible={false}>
							<div className={classNames.anomalySummaryItemData}>
								<div className={classNames.anomalySummaryItemDataBlock}>
									<div className={classNames.anomalySummaryItemDataBlockHeader}>Anomaly Description</div>
									<div className={classNames.anomalySummaryItemDataBlockBody}>
										<span>{pipingItem.inspectionDescription}</span>
									</div>
								</div>
								<div className={classNames.anomalySummaryItemDataBlock}>
									<div className={classNames.anomalySummaryItemDataBlockHeader}>Photo Ref.</div>
									<div className={concatClassNames([classNames.anomalySummaryItemDataBlockBody, 'text-center'])}>
										<span>{formatNumberRange(pipingItem.generalPhotos.map((p) => p.refNo))}</span>
									</div>
								</div>
							</div>
						</Conditional>
					</div>
				</CustomSection>
			</Conditional>
		</React.Fragment>
    );
}
 
export default PipingAnomalySummaryItem;